import {
  BookCopy,
  ChevronLeft,
  ChevronRight,
  Cog,
  DoorOpen,
  Flag,
  Megaphone,
  ListOrdered,
  Menu,
  NotepadText,
  Pen,
  PenSquareIcon,
  Square,
  Strikethrough,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  useGetQuestionByQuestionIdQuery,
  useGetQuestionImageByIdQuery,
  useViewPerscriptionAnswerMutation,
} from "../api/questionsSlice/questionsSlice";
import CheckBox from "../components/checkbox/checkbox";
import Checkbox from "../components/checkbox/checkbox";
import Modal from "../components/modal/Modal";
import ExamTimer from "../components/examTimer/examTimer";
import SettingsBar from "../components/settingsBar/settingsBar";
import QuestionList from "../components/questionList/questionList";
import ImageZoomModal from "../components/imageZoomModal/imageZoomModal";
import { useDispatch, useSelector } from "react-redux";
import {
  addTestExtraSeconds,
  getCreatedTest,
  getTestOutOfTime,
  getWhenTestRanOutOfTime,
  setReviewedExam,
} from "../store/test.slice";
import QuestionNavigator from "../components/navigator/Navigator";
import { useSubmitFeedbackMutation } from "../api/feedbackSlice/feedbackSlice";
import useTokenSpoilCheck from "../hooks/tokenspoilcheck";
import UniChart from "../components/UniChart/unichart";
import ExamSaver from "../components/examSaver/examSaver";
import {
  getDistanceToStartOfContainerBreak,
  getSelectedContainers,
  mergeTextRanges,
  processQuestionText,
  GetUnmodifiedAnswerArray,
} from "../helpers/testHelpers";
import TestCenterColumns from "../components/testCenterColumns/testCenterColumns";
import { Warnings } from "../constants/test";
import {
  getModelFetchingData,
  getReviewHighYield,
  getTestSettings,
  getUserData,
  getUserDataTheme,
  hideSidebar,
  setReviewHighYield,
} from "../store/login.slice";
import { toast } from "react-toastify";
import {
  useGardeTestMutation,
  useUpdateHighyieldMutation,
  useUpdateReviewLaterMutation,
} from "../api/testSlice.ts/testSlice";
import { useNavigate, useParams } from "react-router-dom";
import { darkThemes } from "../constants/themes";
import styles from "./test.module.scss";
/*
 * Review mode: the completed test review (/reviewTest) renders this same page
 * with a `review` prop. Everything looks the same; answers are read only and
 * revealed, there is no timer or saving, and "Exit Test" leaves the review.
 */
export type ReviewModeItem = {
  kind: "mc" | "rx" | "none";
  grade?: any; // Grades entry (mc) or PrescriptionGrades entry (rx)
  score?: number; // 0-100
};
export type ReviewModeData = {
  createdExam: any; // same shape as the created test in the store
  itemsById: Record<string, ReviewModeItem>;
  overallGrade?: number;
  onExit: () => void;
};

const PASS_MARK = 70;

// Shown for a prescription question that was never answered
const UNANSWERED_PRESCRIPTION = (() => {
  const field = {
    Points: 0,
    Color: "red",
    YourAnswer: "No answer",
    CorrectAnswer: "Not available",
  };
  return {
    Drug: field,
    Strength: {
      Points: 0,
      Color: "red",
      YourStrength: "No answer",
      CorrectStrength: "Not available",
    },
    Disp: field,
    Repeat: field,
    Sig: {
      Points: 0,
      ComponentResults: {},
      Feedback: ["Incorrect: no prescription was written for this question"],
    },
  };
})();

function Test({ review }: { review?: ReviewModeData } = {}) {
  const isReview = review != undefined;
  //
  //Overrides TODO DISABLE BEFORE RELEASE
  //
  const { questionId } = useParams(); // Get questionId from the URL
  //
  //Css Override functionality
  //
  const [sizing, setSizing] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  function handleWindowSizeChange() {
    setSizing({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }
  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    handleWindowSizeChange();
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);
  const theme = useSelector(getUserDataTheme);
  const inDarkMode = darkThemes.includes(theme);
  //Experimental CSS override, normally not acceptable but works well enough for now.
  const centerColumSize = sizing.height * 0.85 - (1080 - sizing.height) * 0.15;
  const centerColumSizeReview =
    sizing.height < 1080
      ? sizing.height * 0.883 - (1080 - sizing.height) * 0.15
      : sizing.height * 0.883 - (1080 - sizing.height) * 0.1;

  //
  //Mobile mode
  //
  const isMobile = sizing.width <= 640;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  //
  //Hooks
  //
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //
  //Selectors
  //
  const userData = useSelector(getUserData);
  const storeCreatedExam = useSelector(getCreatedTest);
  const createdExam = isReview ? review.createdExam : storeCreatedExam;
  const reviewHighYield = useSelector(getReviewHighYield);
  const testOutOfTime = useSelector(getTestOutOfTime);
  const whenTestRanOutOfTime = useSelector(getWhenTestRanOutOfTime);
  const modelFetchingData = useSelector(getModelFetchingData);
  const testSettings = useSelector(getTestSettings);
  //Untimed tests have no timer at all, so they can never run out of time.
  //Tests made before the Timed flag existed have no timedMode and stay timed.
  const isTimed = !isReview && createdExam.timedMode !== false;
  //
  //Data
  //
  let modifiedKeyIndexes = 0;
  const maxQuestions =
    questionId != undefined
      ? ["overrideQuestion"]
      : createdExam.QuestionsForTest.length;
  const initialScrollState = Array(maxQuestions).fill({
    left: false,
    right: false,
  });

  //Initial state data creation
  const initialAnswerData = createdExam.QuestionsForTest.reduce(
    (acc, question) => {
      acc[question] = [];
      return acc;
    },
    {},
  );

  const initialNotesData = createdExam.QuestionsForTest.reduce(
    (acc, question) => {
      acc[question] = "";
      return acc;
    },
    {},
  );

  const initialReviewData = createdExam.QuestionsForTest.reduce(
    (acc, question) => {
      acc[question] = false;
      return acc;
    },
    {},
  );

  const initialMarkedData = createdExam.QuestionsForTest.reduce(
    (acc, question) => {
      acc[question] = false;
      return acc;
    },
    {},
  );

  let initialAnswerChoices = {};
  const initialAnswerOrders = {};
  const initialAnswerChanges = {};
  const initialTextModifications = {};
  let initialTutorialMode = [];
  if (createdExam?.CapturedData?.notesById) {
    Object.keys(createdExam.CapturedData.notesById).forEach((key) => {
      initialNotesData[key] = createdExam.CapturedData.notesById[key];
    });
  }
  if (createdExam?.CapturedData?.reviewById) {
    Object.keys(createdExam.CapturedData.reviewById).forEach((key) => {
      initialReviewData[key] = createdExam.CapturedData.reviewById[key];
    });
  }

  if (createdExam?.CapturedData?.answersById) {
    Object.keys(createdExam.CapturedData.answersById).forEach((key) => {
      initialAnswerData[key] = createdExam.CapturedData.answersById[key];
    });
  }

  if (createdExam?.AnswerChoicesForTest) {
    const result = createdExam.AnswerChoicesForTest.reduce((acc, item) => {
      acc[item.question_id] = item.answer_serials;
      return acc;
    }, {});
    initialAnswerChoices = { ...result };
  } else if (createdExam?.CapturedData?.answerChoices) {
    Object.keys(createdExam.CapturedData.answerChoices).forEach((key) => {
      initialAnswerChoices[key] = createdExam.CapturedData.answerChoices[key];
    });
  }

  if (createdExam?.CapturedData?.answerOrdersById) {
    Object.keys(createdExam.CapturedData.answerOrdersById).forEach((key) => {
      initialAnswerOrders[key] = createdExam.CapturedData.answerOrdersById[key];
    });
  }

  if (createdExam?.CapturedData?.answerChangesById) {
    Object.keys(createdExam.CapturedData.answerChangesById).forEach((key) => {
      initialAnswerChanges[key] =
        createdExam.CapturedData.answerChangesById[key];
    });
  }

  if (createdExam?.CapturedData?.textModificationsById) {
    Object.keys(createdExam.CapturedData.textModificationsById).forEach(
      (key) => {
        initialTextModifications[key] =
          createdExam.CapturedData.textModificationsById[key];
      },
    );
  }

  if (createdExam?.CapturedData?.tutorialMode) {
    initialTutorialMode = createdExam.CapturedData.tutorialMode;
  }

  //DLOSCE prescription picks: { [questionId]: [{ med_id, classification, regimen_id }] }
  const initialDloscePrescriptions =
    createdExam?.CapturedData?.dloscePrescriptionsById ?? {};

  //End initial data creation

  //
  //Local state hooks
  //
  const [errorStatusCode, setErrorStatusCode] = useState(undefined);
  useTokenSpoilCheck(errorStatusCode);
  const [exitExamOpen, setExitExamOpen] = useState(false);
  const [scrollChecks, setScrollChecks] = useState(initialScrollState);
  const [examinationState, setExaminationState] = useState({
    currentQuestionIndex: 0,
    reviewMode: false,
    viewAnswerMode: false,
    reviewingIncompleteQuestions: false,
    reviewingAllQuestions: false,
    reviewingMarkedQuestions: false,
    answersById: { ...initialAnswerData },
    notesById: { ...initialNotesData },
    textModificationsById: { ...initialTextModifications },
    reviewById: { ...initialReviewData },
    //Unused: "Mark for review" uses reviewById everywhere (top bar, question
    //list, review screen, Review Marked). Kept so saved test data keeps its shape.
    markedById: { ...initialMarkedData },
    answerChangesById: { ...initialAnswerChanges },
    answerOrdersById: { ...initialAnswerOrders },
    answerChoices: { ...initialAnswerChoices },
    tutorialMode: [...initialTutorialMode],
    prescriptions: {},
    dloscePrescriptionsById: { ...initialDloscePrescriptions },
    incompleteToBeReviewed: [],
  });
  const currentQuestionId =
    questionId != undefined
      ? questionId
      : createdExam.QuestionsForTest[examinationState.currentQuestionIndex]; //Not a state but important to keep around to shorten many lines*/
  //Tutor mode: once a question's answer has been viewed it stays revealed
  //inline in the center columns (no more "Viewing Answer" modal)
  const answerRevealed =
    !isReview &&
    !!createdExam.tutorMode &&
    examinationState.tutorialMode.includes(currentQuestionId);
  const [warningsState, setWarningsState] = useState({
    incomplete: false,
    view: false,
    end: false,
  });
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  //Feedback modal: status is "idle" | "loading" | "success" | "error"
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    status: "idle",
  });
  const [navigator, setNavigator] = useState({
    open: false,
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  //Question number sidebar, open by default except on phones
  const [questionListOpen, setQuestionListOpen] = useState(
    window.innerWidth > 640,
  );
  const [zoomedImageSource, setZoomedImageSource] = useState<string | null>(
    null,
  );
  const [textEditModes, setTextEditModes] = useState({
    highlight: false,
    strikeThrough: false,
  });

  //
  //Queries and mutatios
  //

  const {
    data: currentQuestion,
    isFetching,
    isLoading,
    isError: questionIsError,
    error: questionError,
  } = useGetQuestionByQuestionIdQuery({
    question_id: currentQuestionId,
    choices: examinationState.answerChoices,
  });

  const {
    data: currentQuestionImage,
    isLoading: questionImageFirstLoad,
    isFetching: questionImageLoading,
  } = useGetQuestionImageByIdQuery({
    question_id: currentQuestionId,
  });

  const imageSource =
    currentQuestionImage && currentQuestionImage.type.startsWith("image/")
      ? URL.createObjectURL(currentQuestionImage)
      : null;

  // Clicking the question image opens the full-screen zoom viewer.
  // The image is rendered inside TestCenterColumns, so the click is picked up
  // here from the wrapper around it instead.
  // TestCenterColumns revokes its blob URL as soon as the image loads, so the
  // viewer gets its own fresh URL made from the image data (freed on close).
  const handleQuestionImageClick = (event) => {
    const target = event.target;
    if (
      target instanceof HTMLImageElement &&
      target.src.startsWith("blob:") &&
      currentQuestionImage &&
      currentQuestionImage.type.startsWith("image/")
    ) {
      setZoomedImageSource(URL.createObjectURL(currentQuestionImage));
    }
  };

  const closeImageZoom = () => {
    if (zoomedImageSource) URL.revokeObjectURL(zoomedImageSource);
    setZoomedImageSource(null);
  };

  const [gardeTest] = useGardeTestMutation();

  //
  //Review mode data and actions
  //
  const [updateHighyield] = useUpdateHighyieldMutation();
  const [updateReviewLater] = useUpdateReviewLaterMutation();
  const currentReviewItem: ReviewModeItem | undefined =
    review?.itemsById[String(currentQuestionId)];
  const reviewQuestionId = Number(currentQuestionId);
  const isHighYield =
    reviewHighYield?.HighYieldQuestions?.includes(reviewQuestionId) ?? false;
  const isReviewLater =
    reviewHighYield?.MarkedQuestions?.includes(reviewQuestionId) ?? false;

  const toggleHighYield = async () => {
    try {
      const result = await updateHighyield({
        data: { question_id: reviewQuestionId, high_yield: !isHighYield },
      }).unwrap();
      dispatch(setReviewHighYield({ ...result.ReviewHighYield }));
    } catch (error) {
      setErrorStatusCode(error?.status);
      toast(error?.data?.message ?? "Couldn't update High Yield");
    }
  };

  const toggleReviewLater = async () => {
    try {
      const result = await updateReviewLater({
        data: { question_id: reviewQuestionId, marked: !isReviewLater },
      }).unwrap();
      dispatch(setReviewHighYield({ ...result.ReviewHighYield }));
    } catch (error) {
      setErrorStatusCode(error?.status);
      toast(error?.data?.message ?? "Couldn't update Review Later");
    }
  };

  //"Mark for review": local flag in a test, the saved Review Later flag in a review
  const markedForReview = isReview
    ? isReviewLater
    : !!examinationState.reviewById[currentQuestionId];
  const toggleMarkForReview = () => {
    if (isReview) {
      toggleReviewLater();
    } else {
      handleReviewCheckboxChecked();
    }
  };

  //In a review the answers are revealed exactly like "View Answer"
  const reviewQuestionType =
    currentQuestion?.GetQuestion?.question_type ??
    (currentReviewItem?.kind === "rx" ? "Prescription" : undefined);
  const reviewPrescriptionData =
    isReview && reviewQuestionType === "Prescription"
      ? currentReviewItem?.kind === "rx"
        ? currentReviewItem.grade
        : UNANSWERED_PRESCRIPTION
      : undefined;

  //
  //Feedback (same payload as the other apps, sent as OSCEDENTAL)
  //
  const [sendFeedback] = useSubmitFeedbackMutation();

  const openFeedback = () => {
    setFeedback({ open: true, message: "", status: "idle" });
  };

  const closeFeedback = () => {
    if (feedback.status === "loading") return;
    setFeedback({ open: false, message: "", status: "idle" });
  };

  const submitFeedback = async () => {
    const text = feedback.message.trim();
    if (!text || feedback.status === "loading") return;
    setFeedback((prev) => ({ ...prev, status: "loading" }));

    const contextText = [`Question ID: ${currentQuestionId}`]
      .filter(Boolean)
      .join("\n");
    const payload = {
      customer_name:
        userData?.customer_first_name + " " + userData?.customer_last_name,
      product: "OSCEDENTAL",
      customer_id: userData?.customer_id,
      customer_email: userData?.customer_email,
      case_number: 0,
      question_id: currentQuestionId,
      message: contextText ? `${text}\n\n---\n${contextText}` : text,
      reviewed: false,
      card_number_id: null,
      key: "OSCEDENTAL",
    };

    try {
      await sendFeedback(payload).unwrap();
      setFeedback((prev) => ({ ...prev, status: "success" }));
      setTimeout(() => {
        setFeedback({ open: false, message: "", status: "idle" });
      }, 1800);
    } catch (error) {
      console.error("Error sending feedback:", error);
      setErrorStatusCode(error?.status);
      setFeedback((prev) => ({ ...prev, status: "error" }));
    }
  };

  const isModelLoading =
    modelFetchingData[currentQuestion?.GetQuestion?.image_path]?.loading ||
    false;

  //
  //useEffect hooks
  //
  useEffect(() => {
    const choices = [];
    if (
      currentQuestion?.GetAnswer &&
      examinationState.answerChoices[currentQuestion.GetQuestion.question_id] ==
        undefined
    ) {
      currentQuestion?.GetAnswer.forEach((element) => {
        if (element.answer_serial) {
          choices.push(element.answer_serial);
        } else {
          console.error("NO SERIAL PRESENT");
        }
      });

      if (currentQuestion) {
        setExaminationState((prevState) => ({
          ...prevState,
          answerChoices: {
            ...prevState.answerChoices,
            [currentQuestion.GetQuestion.question_id]: [...choices],
          },
        }));
      }
    }
  }, [currentQuestion]);

  useEffect(() => {
    handleWindowSizeChange();
  }, [navigator.open]);

  const [
    viewPerscriptionAnswer,
    { data: prescriptionAnswerData, isLoading: prescriptionAnswerLoading },
  ] = useViewPerscriptionAnswerMutation();

  useEffect(() => {
    const fetchPrescriptionAnswer = async () => {
      if (
        answerRevealed &&
        currentQuestion?.GetQuestion?.question_type === "Prescription" &&
        currentQuestionId &&
        examinationState?.prescriptions?.[currentQuestionId]
      ) {
        const currentPrescription =
          examinationState.prescriptions[currentQuestionId][0];

        if (currentPrescription.disp === "") currentPrescription.disp = 0;
        if (currentPrescription.repeat === "") currentPrescription.repeat = 0;

        try {
          await viewPerscriptionAnswer({
            prescriptions: {
              [currentQuestionId]: [currentPrescription],
            },
          });
        } catch (error) {
          console.error("Error fetching prescription answer:", error);
        }
      }
    };

    fetchPrescriptionAnswer();
  }, [
    answerRevealed,
    currentQuestion?.GetQuestion?.question_type,
    currentQuestionId,
    examinationState?.prescriptions,
    viewPerscriptionAnswer,
  ]);

  //
  //Copy protection
  //
  useEffect(() => {
    // Disable right-click context menu
    const disableContextMenu = (event) => event.preventDefault();
    document.addEventListener("contextmenu", disableContextMenu);

    // Disable copy and cut keyboard shortcuts
    const disableCopyCut = (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "c" || event.key === "x")
      ) {
        event.preventDefault();
      }
    };
    document.addEventListener("keydown", disableCopyCut);

    // Disable the copy event
    const disableCopyEvent = (event) => {
      event.preventDefault();
    };
    document.addEventListener("copy", disableCopyEvent);

    // Cleanup event listeners on unmount
    return () => {
      document.removeEventListener("contextmenu", disableContextMenu);
      document.removeEventListener("keydown", disableCopyCut);
      document.removeEventListener("copy", disableCopyEvent);
    };
  }, []);

  // Refresh protection

  useEffect(() => {
    if (isReview) return; // nothing to lose in a review
    const handleBeforeUnload = (event) => {
      const message =
        "Refreshing this page will return you to the login page. Are you sure you want to leave this test? You can continue this test from the incomplete tests page.";
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  //
  //Util functions
  //
  //Update notes logic
  //
  const handleNotesChange = (event) => {
    setExaminationState((prevState) => ({
      ...prevState,
      notesById: {
        ...prevState.notesById,
        [currentQuestionId]: event.target.value,
      },
    }));
  };

  //
  //Text mod logic
  //

  const handleTextSelection = useCallback(
    (event?: Event) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      //Only react to selections made on question text. Clicking a button
      //(Next, Strike through, an answer checkbox...) must not re-process
      //whatever caret/selection happens to be lying around.
      const target = event?.target as Element | null;
      if (
        target instanceof Element &&
        target.closest("button, input, textarea, select, a, [role='button']")
      ) {
        return;
      }

      const currentMode = textEditModes.highlight
        ? "highlight"
        : "strikethrough";
      const selectedTextByContainer: Record<
        string,
        { start: number; end: number; mode: string }
      > = {};
      let removeMode = false;

      //Closest element with a "real" data-id (not the break or a split span)
      const getParentWithDataId = (node) => {
        while (node) {
          const id = node.getAttribute?.("data-id");
          if (
            id != null &&
            id !== "container-break" &&
            !id.includes("sortingOrder")
          ) {
            return node;
          }
          node = node.parentNode;
        }
        return null;
      };

      for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        const containers = getSelectedContainers(range);

        containers.forEach((container: Node) => {
          const parentWithDataId = getParentWithDataId(container);
          const distance = getDistanceToStartOfContainerBreak(container);
          //Not question text (no data-id / no container-break): skip it
          //instead of throwing and leaving the selection stuck.
          if (!parentWithDataId || distance == null) return;

          const textLength = container.textContent?.length ?? 0;
          //Offsets inside this text node. startOffset/endOffset only belong
          //to the node they were taken from; every other node is either
          //fully selected or starts/ends at its own edge.
          const localStart =
            container === range.startContainer ? range.startOffset : 0;
          const localEnd =
            container === range.endContainer ? range.endOffset : textLength;

          const start = distance + localStart;
          const end = distance + localEnd;
          const containerId = parentWithDataId.getAttribute("data-id");

          if (range.collapsed) {
            //A plain click on struck/highlighted text removes that mark
            removeMode = true;
          }

          if (!selectedTextByContainer[containerId]) {
            selectedTextByContainer[containerId] = {
              start,
              end,
              mode: currentMode,
            };
          } else {
            const existing = selectedTextByContainer[containerId];
            existing.start = Math.min(existing.start, start);
            existing.end = Math.max(existing.end, end);
          }
        });
      }

      const entries = Object.entries(selectedTextByContainer);
      if (entries.length === 0) return; //selection wasn't on question text

      setExaminationState((prevState) => {
        //Copy everything we touch so we never mutate the previous state
        //(React StrictMode runs this updater twice in dev).
        const previousForQuestion =
          prevState.textModificationsById[currentQuestionId] || {};
        const textModificationsForQuestion = { ...previousForQuestion };

        if (removeMode) {
          const [parentId, { start }] = entries[0];
          const existing = textModificationsForQuestion[parentId];
          if (existing) {
            textModificationsForQuestion[parentId] = existing.filter(
              (range) => !(range.start <= start && range.end >= start),
            );
          }
        } else {
          entries.forEach(([parentId, { start, end, mode }]) => {
            if (end <= start) return;
            textModificationsForQuestion[parentId] = mergeTextRanges(
              textModificationsForQuestion[parentId] || [],
              { start, end, mode },
            );
          });
        }

        return {
          ...prevState,
          textModificationsById: {
            ...prevState.textModificationsById,
            [currentQuestionId]: textModificationsForQuestion,
          },
        };
      });

      selection.removeAllRanges();
    },
    [currentQuestionId, textEditModes.highlight],
  );

  //Mobile: the selection isn't final yet when touchend fires, so wait a tick
  const handleTouchSelection = useCallback(
    (event: Event) => {
      setTimeout(() => handleTextSelection(event), 0);
    },
    [handleTextSelection],
  );

  //Highlighter button hidden from the settings bar -> leave highlight mode too
  useEffect(() => {
    if (!testSettings.highlighter && textEditModes.highlight) {
      setTextEditModes((prev) => ({ ...prev, highlight: false }));
    }
  }, [testSettings.highlighter, textEditModes.highlight]);

  //Same for the strike through button
  useEffect(() => {
    if (!testSettings.strikethrough && textEditModes.strikeThrough) {
      setTextEditModes((prev) => ({ ...prev, strikeThrough: false }));
    }
  }, [testSettings.strikethrough, textEditModes.strikeThrough]);

  //Highlighting logic
  useEffect(() => {
    if (!textEditModes.highlight && !textEditModes.strikeThrough) return;
    document.addEventListener("mouseup", handleTextSelection);
    document.addEventListener("touchend", handleTouchSelection);
    return () => {
      document.removeEventListener("mouseup", handleTextSelection);
      document.removeEventListener("touchend", handleTouchSelection);
    };
  }, [
    handleTextSelection,
    handleTouchSelection,
    textEditModes.highlight,
    textEditModes.strikeThrough,
  ]);

  // Helper function to apply highlighting for all highlighted texts
  const getHighlightedText = (text, parentId) => {
    if (typeof text == "number") {
      text = String(text);
    }
    // Retrieve highlighted ranges for the specific parentId under the currentQuestionIndex
    //const currentQuestionIndex = examinationState.currentQuestionIndex;
    const highlightedRanges = [
      ...(examinationState.textModificationsById[currentQuestionId]?.[
        parentId
      ] || []),
    ];

    // If no highlighted ranges for this element, return original trimmed text
    if (!highlightedRanges || highlightedRanges.length === 0) return text;

    // Create an array to hold parts of the text with highlighting
    const parts = [];
    let lastIndex = 0;
    const sortedRanges = highlightedRanges.sort((a, b) => a.start - b.start);
    // Iterate through the highlighted ranges
    sortedRanges.forEach(({ start: rawStart, end, mode }) => {
      // Skip anything already rendered so overlapping ranges can't duplicate text
      const start = Math.max(rawStart, lastIndex);
      if (end <= start) return;
      // Add text before the current highlight

      if (lastIndex < start) {
        modifiedKeyIndexes++;
        parts.push(
          <span
            data-id={`${parentId}-sortingOrder:${lastIndex}`}
            key={`${parentId}-sortingOrder:${modifiedKeyIndexes}`}
          >
            {text.slice(lastIndex, start)}
          </span>,
        );
      }

      // Add the highlighted part of the text
      modifiedKeyIndexes++;
      parts.push(
        <span
          data-id={`${parentId}-sortingOrder:${start}`}
          key={`${parentId}-sortingOrder:${modifiedKeyIndexes}`}
          style={
            mode == "highlight"
              ? { backgroundColor: inDarkMode ? "#654321" : "yellow" }
              : { textDecoration: "line-through" }
          }
        >
          {text.slice(start, end)}
        </span>,
      );

      // Update the last index (never go backwards on overlapping ranges)
      lastIndex = Math.max(lastIndex, end);
    });

    // Add any remaining text after the last highlight
    if (lastIndex < text.length) {
      modifiedKeyIndexes++;
      parts.push(
        <span
          data-id={`${parentId}-sortingOrder:${lastIndex}`}
          key={`${parentId}-sortingOrder:${modifiedKeyIndexes}`}
        >
          {text.slice(lastIndex)}
        </span>,
      );
    }
    // Return a single element that represents the entire text with highlights
    return <>{parts}</>;
  };

  //
  //Answering logic
  //

  const handleAnswerChecked = (serial) => {
    if (isReview) {
      toast("Answers can't be changed in a review");
      return;
    }
    //const currentQuestionIndex = examinationState.currentQuestionIndex;

    if (examinationState.tutorialMode.includes(currentQuestionId)) {
      toast("You have already viewed the answer for this question");
      return;
    }

    const included =
      examinationState.answersById[currentQuestionId] != undefined &&
      examinationState.answersById[currentQuestionId].includes(serial);

    // Get the current question index and answer changes for this question
    const currentAnswerHistory =
      examinationState.answerChangesById[currentQuestionId] || [];

    // Create a new copy of the currentAnswerHistory to avoid mutating state
    const newAnswerHistory = [...currentAnswerHistory];

    if (included) {
      // Answer was already selected, remove it from the selected list and mark the choice as false
      newAnswerHistory.push({
        id: serial,
        choice: false,
      });

      const updatedAnswerById = { ...examinationState.answersById };

      const currentQuestionAnswers = updatedAnswerById[currentQuestionId];

      updatedAnswerById[currentQuestionId] = currentQuestionAnswers.filter(
        (choice) => choice !== serial,
      );

      // Update state by removing the choice from the selected answers
      setExaminationState((prevState) => ({
        ...prevState,
        answersById: { ...updatedAnswerById },
        answerChangesById: {
          ...prevState.answerChangesById,
          [currentQuestionId]: newAnswerHistory,
        },
      }));
    } else {
      // Answer was not selected, add it to the selected list and mark the choice as true
      newAnswerHistory.push({
        id: serial,
        choice: true,
      });

      const updatedAnswerById = { ...examinationState.answersById };
      // deep clone
      let updatedAnswers = [...updatedAnswerById[currentQuestionId]]; // Clone

      if (currentQuestion?.GetQuestion?.select_one_only) {
        updatedAnswers = [];
      }

      updatedAnswers.push(serial);

      updatedAnswerById[currentQuestionId] = updatedAnswers;

      // Update state with the new selected answers and answer changes
      setExaminationState((prevState) => ({
        ...prevState,
        answersById: updatedAnswerById,
        answerChangesById: {
          ...prevState.answerChangesById,
          [currentQuestionId]: newAnswerHistory,
        },
      }));
    }
  };

  //
  //Answer shuffling logic
  //

  function GetShuffledAnswerArray() {
    const currentQuestionIndex = examinationState.currentQuestionIndex;
    const answerOrderExists =
      examinationState.answerOrdersById[currentQuestionIndex] != undefined;
    const answerArray = currentQuestion.GetAnswer;
    if (answerOrderExists) {
      const order = examinationState.answerOrdersById[currentQuestionIndex];
      // Sort the answers based on the saved order
      const mapped = order.map((index) =>
        answerArray.find((answer) => answer.answer_serial === index),
      );
      return mapped;
    } else {
      const serials = answerArray.map((answer) => answer.answer_serial);

      for (let i = serials.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [serials[i], serials[j]] = [serials[j], serials[i]];
      }

      examinationState.answerOrdersById[currentQuestionIndex] = serials;

      // Call the function again to apply the shuffled order
      return GetShuffledAnswerArray();
    }
  }

  //
  //Reviewing logic
  //

  function handleReviewCheckboxChecked() {
    const { reviewById } = examinationState;

    const newReviewValue = !reviewById[currentQuestionId];

    const updatedReviewById = {
      ...reviewById,
      [currentQuestionId]: newReviewValue,
    };

    setExaminationState((prevState) => ({
      ...prevState,
      reviewById: updatedReviewById,
    }));
  }

  /*function handleReviewCheckboxCheckedByIndex(index) {
    const { reviewById } = examinationState;

    const newReviewValue = !reviewById[index];

    const updatedReviewById = {
      ...reviewById,
      [index]: newReviewValue,
    };

    setExaminationState((prevState) => ({
      ...prevState,
      reviewById: updatedReviewById,
    }));
  }*/

  //Review screen checkboxes: same flag as the top bar "Mark for review"
  function handleMarkCheckboxCheckedByIndex(id) {
    setExaminationState((prevState) => ({
      ...prevState,
      reviewById: {
        ...prevState.reviewById,
        [id]: !prevState.reviewById[id],
      },
    }));
  }

  //
  //Question Movement logic and data
  //

  function NextButtonPressed() {
    if (isReview) {
      if (
        examinationState.currentQuestionIndex <
        createdExam.QuestionsForTest.length - 1
      ) {
        MoveToNextPage();
      }
      return;
    }
    if (
      (!examinationState.reviewingMarkedQuestions &&
        examinationState.currentQuestionIndex <
          createdExam.QuestionsForTest.length - 1) ||
      (examinationState.reviewingMarkedQuestions &&
        markedKeyIndex + 1 < markedCount) ||
      //Tutor mode: Next still reveals the answer on the last question
      tutorRevealPending()
    ) {
      //"Scroll the whole question" warning: account level Warnings setting
      if (!userData.hide_warning) {
        if (
          !scrollChecks[examinationState.currentQuestionIndex].left ||
          !scrollChecks[examinationState.currentQuestionIndex].right
        ) {
          setWarningsState({ incomplete: false, view: true, end: false });
          return;
        }
      }
      //"Move on without answering?" warning: settings bar Confirm Answer Omission
      if (
        testSettings.confirmAnswerOmission &&
        !currentQuestionFullyAnswered()
      ) {
        setWarningsState({
          incomplete: true,
          view: false,
          end: false,
        });
        return;
      }
      MoveForward();
    }
  }

  //Moves to the next question the way the current review mode expects
  //(used by Next and by "Yes" on the unanswered question warning)
  function MoveForward() {
    if (examinationState.reviewingMarkedQuestions) {
      MoveToNextMarkedPage();
    } else if (examinationState.reviewingIncompleteQuestions) {
      MoveToNextUnansweredPage();
    } else if (tutorRevealPending()) {
      //Tutor mode: the first Next reveals the answer in place,
      //the second Next moves on to the next question
      handleViewAnswer();
    } else if (
      examinationState.currentQuestionIndex <
      createdExam.QuestionsForTest.length - 1
    ) {
      MoveToNextPage();
    }
  }

  //Tutor mode: answered, but the answer hasn't been revealed yet
  //(multiple choice and DLOSCE prescriptions)
  function tutorRevealPending() {
    return (
      !!createdExam.tutorMode &&
      !examinationState.reviewingMarkedQuestions &&
      !examinationState.reviewingIncompleteQuestions &&
      !answerRevealed &&
      currentQuestionAttempted()
    );
  }

  function MoveToNextPage() {
    setExaminationState((prevState) => {
      return {
        ...prevState,
        currentQuestionIndex: prevState.currentQuestionIndex + 1,
      };
    });
  }
  //Marked
  //Marked questions in test order (object keys would come back sorted by id)
  const markedKeys = createdExam.QuestionsForTest.filter(
    (id) => !!examinationState.reviewById[id],
  );

  const markedCount = markedKeys.length;

  const markedKeyIndex = markedKeys.findIndex(
    (key) => key == currentQuestionId,
  );

  const nextMarkedKeyIndex = createdExam.QuestionsForTest.findIndex(
    (key) => key == markedKeys[markedKeyIndex + 1],
  );

  const previousMarkedKeyIndex = createdExam.QuestionsForTest.findIndex(
    (key) => key == markedKeys[markedKeyIndex - 1],
  );

  //Unanswered ones
  const emptyKeysUnsorted = examinationState.incompleteToBeReviewed;
  const emptyKeys = createdExam.QuestionsForTest.filter((item) =>
    emptyKeysUnsorted.includes(item),
  );

  const incompleteCount = emptyKeys.length;

  const currentUnasnweredIndex = emptyKeys.findIndex(
    (key) => key == currentQuestionId,
  );

  const nextUnansweredKeyIndex = createdExam.QuestionsForTest.findIndex(
    (key) => key == emptyKeys[currentUnasnweredIndex + 1],
  );

  const previousUnasnweredKeyIndex = createdExam.QuestionsForTest.findIndex(
    (key) => key == emptyKeys[currentUnasnweredIndex - 1],
  );

  function MoveToNextUnansweredPage() {
    if (nextUnansweredKeyIndex == -1) {
      //console.error("No more unanswered questions");
      return;
    }
    setExaminationState((prevState) => {
      return {
        ...prevState,
        currentQuestionIndex: nextUnansweredKeyIndex,
      };
    });
  }

  function MoveToPreviousUnansweredPage() {
    if (previousUnasnweredKeyIndex == -1) {
      //console.error("No more unanswered questions");
      return;
    }
    setExaminationState((prevState) => {
      return {
        ...prevState,
        currentQuestionIndex: previousUnasnweredKeyIndex,
      };
    });
  }

  function MoveToNextMarkedPage() {
    if (nextMarkedKeyIndex == -1) {
      //console.log("No more marked questions");
      return;
    }
    setExaminationState((prevState) => {
      return {
        ...prevState,
        currentQuestionIndex: nextMarkedKeyIndex,
      };
    });
  }

  function MoveToPreviousMarkedPage() {
    if (previousMarkedKeyIndex == -1) {
      //console.log("No more marked questions");
      return;
    }
    setExaminationState((prevState) => {
      return {
        ...prevState,
        currentQuestionIndex: previousMarkedKeyIndex,
      };
    });
  }

  function MoveToReviewMode() {
    setExaminationState((prevState) => {
      return {
        ...prevState,
        reviewMode: true,
        //Kept false for older saved tests that still carry the old modal flag
        viewAnswerMode: false,
      };
    });
  }

  //
  //Tutor mode functionality
  //
  //Reveals the answer inline: adding the question to tutorialMode locks its
  //answers and makes the center columns show checks, explanations and summary
  function handleViewAnswer() {
    setExaminationState((prevState) =>
      prevState.tutorialMode.includes(currentQuestionId)
        ? prevState
        : {
            ...prevState,
            tutorialMode: [...prevState.tutorialMode, currentQuestionId],
          },
    );
  }

  //
  //End Exam logic
  //

  async function SaveAndendExam() {
    const result = await gardeTest({
      testData: { guid: createdExam.guid },
    }).unwrap();
    const prevData =
      result.CompletedTests[
        result.CompletedTests.findIndex(
          (completedExam) => completedExam.guid == createdExam.guid,
        )
      ];
    const examToReview = {
      test: result,
      guid: createdExam.guid,
      prevData,
    };
    dispatch(
      setReviewedExam({
        examToReview: examToReview,
      }),
    );
    dispatch(hideSidebar());
    const reviewObject = result.ReviewHighYield;
    dispatch(setReviewHighYield({ ...reviewObject }));
    navigate("/reviewTest");
  }

  function AddMoreTime() {
    const milisecondsWhenTestRanOutOfTime = whenTestRanOutOfTime;
    //Figure out how much time has passed since the target time was reached we're doing this because the button press on the add more time might not be immediate
    const currentTime = Date.now(); // Current time in milliseconds
    const initialTimeDifference = Math.floor(
      (currentTime - milisecondsWhenTestRanOutOfTime) / 1000,
    ); // Convert to seconds
    //Add five minutes on top of that
    const fiveMinutesSeconds = 300;

    //Add the time
    dispatch(
      addTestExtraSeconds({
        testExtraSeconds: fiveMinutesSeconds + initialTimeDifference,
      }),
    );
  }

  //
  //Utility checkers
  //

  //Prescription: drug, strength, dispense amount and sig filled in (repeat can be 0).
  function prescriptionFilledIn(prescription) {
    return (
      !!prescription &&
      String(prescription.drug ?? "").trim() !== "" &&
      String(prescription.strength ?? "").trim() !== "" &&
      String(prescription.sig ?? "").trim() !== "" &&
      Number(prescription.disp) > 0
    );
  }

  //DLOSCE prescription: every medication has a classification and a regimen.
  //An entry exists for every medication as soon as one drop-down is used.
  function dloscePrescriptionFilledIn(answers) {
    return (
      Array.isArray(answers) &&
      answers.length > 0 &&
      answers.every((a) => !!a.classification && a.regimen_id != null)
    );
  }

  //DLOSCE prescription: at least one drop-down picked.
  function dloscePrescriptionStarted(answers) {
    return (
      Array.isArray(answers) &&
      answers.some((a) => !!a.classification || a.regimen_id != null)
    );
  }

  //Multiple choice: at least one choice picked.
  function currentQuestionFullyAnswered() {
    if (currentQuestion?.GetQuestion?.dlosceTask) {
      return dloscePrescriptionFilledIn(
        examinationState.dloscePrescriptionsById?.[currentQuestionId],
      );
    }
    if (currentQuestion?.GetQuestion?.question_type === "Prescription") {
      return prescriptionFilledIn(
        examinationState.prescriptions?.[currentQuestionId]?.[0],
      );
    }
    return currentQuestionHasAnswers();
  }

  //Tutor mode: has anything been answered on the current question yet.
  //DLOSCE prescriptions count once at least one drop-down is picked.
  function currentQuestionAttempted() {
    if (currentQuestion?.GetQuestion?.dlosceTask) {
      return dloscePrescriptionStarted(
        examinationState.dloscePrescriptionsById?.[currentQuestionId],
      );
    }
    return currentQuestionHasAnswers();
  }

  //Same check for any question in the list. Prescription questions only get a
  //prescriptions entry once they've been opened, so that's how they're told apart.
  function isQuestionAnswered(id) {
    if (isReview) {
      const item = review.itemsById[String(id)];
      if (!item || item.kind === "none") return false;
      return item.kind === "rx" || item.grade?.YourAnswersSerials?.length > 0;
    }
    const dlosceAnswers = examinationState.dloscePrescriptionsById?.[id];
    if (dlosceAnswers) return dloscePrescriptionStarted(dlosceAnswers);
    const prescription = examinationState.prescriptions?.[id]?.[0];
    if (prescription) return prescriptionFilledIn(prescription);
    return examinationState.answersById[id]?.length > 0;
  }

  function isQuestionMarked(id) {
    if (isReview) {
      return reviewHighYield?.MarkedQuestions?.includes(Number(id)) ?? false;
    }
    return !!examinationState.reviewById[id];
  }

  //Review only: ✓ / ✗ in the question list
  function getQuestionResult(id) {
    const score = review?.itemsById[String(id)]?.score;
    return score != undefined && score >= PASS_MARK;
  }

  function currentQuestionHasAnswers() {
    return (
      examinationState.answersById[currentQuestionId] != undefined &&
      examinationState.answersById[currentQuestionId].length != 0
    );
  }

  return (
    <div className={styles.shell}>
      {/* Question list: full height, left edge of the page */}
      {questionListOpen && questionId == undefined && (
        <QuestionList
          questionIds={createdExam.QuestionsForTest}
          currentIndex={examinationState.currentQuestionIndex}
          isAnswered={isQuestionAnswered}
          isMarked={isQuestionMarked}
          getResult={isReview ? getQuestionResult : undefined}
          onSelect={(index) => {
            setExaminationState((prevState) => ({
              ...prevState,
              reviewMode: false,
              currentQuestionIndex: index,
            }));
          }}
          height="100dvh"
        />
      )}
      <div className={styles.page}>
        {/* Top nav bar */}
        <div className={styles.topBar}>
          <div className={styles.column}>
            <span>
              OSCE Dental
              {isReview && " - Review Mode"}
              {(examinationState.reviewingAllQuestions ||
                examinationState.reviewingMarkedQuestions ||
                examinationState.reviewingIncompleteQuestions) &&
                ` - Question id: ${currentQuestionId}`}
            </span>

            {!(
              examinationState.reviewingAllQuestions ||
              examinationState.reviewingMarkedQuestions ||
              examinationState.reviewingIncompleteQuestions
            ) && <span>Question id: {currentQuestionId}</span>}

            <span className={styles.reviewingLabel}>
              {examinationState.reviewingAllQuestions &&
                "Reviewing All Questions"}
              {examinationState.reviewingMarkedQuestions &&
                "Reviewing Marked Questions"}
              {examinationState.reviewingIncompleteQuestions &&
                "Reviewing Incomplete Questions"}
            </span>
          </div>
          {!examinationState.reviewMode && (
            <div className={styles.column}>
              {isReview ? (
                /* Review: scores where the timer normally is */
                <div className={styles.reviewScores}></div>
              ) : !isTimed ? (
                /* Untimed test: no clock */
                <div>Untimed</div>
              ) : (
                /* Hidden, not unmounted, so the clock keeps running */
                <div
                  className={testSettings.showTimer ? undefined : styles.hidden}
                >
                  <ExamTimer
                    currentQuestionIndex={examinationState.currentQuestionIndex}
                    isLoading={
                      isLoading ||
                      isFetching ||
                      isModelLoading ||
                      questionImageFirstLoad ||
                      questionImageLoading ||
                      answerRevealed
                    }
                    numberOfQuestions={createdExam.QuestionsForTest.length}
                  />
                </div>
              )}
              {!examinationState.reviewingMarkedQuestions &&
                !examinationState.reviewingIncompleteQuestions && (
                  <div className={styles.questionCount}>
                    Question: {examinationState.currentQuestionIndex + 1} of{" "}
                    {maxQuestions}{" "}
                  </div>
                )}
              {examinationState.reviewingMarkedQuestions && (
                <div className={styles.questionCount}>
                  Question: {markedKeyIndex + 1} of {markedCount}{" "}
                </div>
              )}
              {examinationState.reviewingIncompleteQuestions && (
                <div className={styles.questionCount}>
                  Question: {currentUnasnweredIndex + 1} of{" "}
                  {incompleteCount}{" "}
                </div>
              )}
            </div>
          )}
          {examinationState.reviewMode && (
            <div className={styles.timerColReview}>
              {isTimed && (
                <div
                  className={testSettings.showTimer ? undefined : styles.hidden}
                >
                  <ExamTimer
                    currentQuestionIndex={examinationState.currentQuestionIndex}
                    isLoading={isLoading}
                    numberOfQuestions={createdExam.QuestionsForTest.length}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {/* mid block */}
        {!examinationState.reviewMode && (
          <div className={styles.midBlock}>
            {/* actions */}
            {isMobile ? (
              <div className={styles.mobileActions}>
                <div className={styles.mobileMenuWrap}>
                  <button
                    className={styles.mobileMenuToggle}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  >
                    <Menu className={styles.menuIcon} />
                  </button>

                  {isMobileMenuOpen && (
                    <div className={styles.mobileMenu}>
                      <div className={styles.mobileMenuHeader}>
                        <img
                          src={"images/dental.png"}
                          className={styles.mobileLogo}
                        />
                        <button
                          className={styles.mobileClose}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          <X className={styles.mobileCloseIcon} />
                        </button>
                      </div>
                      <button
                        className={styles.mobileMenuButton}
                        onClick={() => {
                          setNotesOpen(true);
                          setTextEditModes({
                            highlight: false,
                            strikeThrough: false,
                          });
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <NotepadText className={styles.notesIcon} />
                        <span className={styles.normalText}>Notes</span>
                      </button>
                      {testSettings.highlighter && (
                        <button
                          className={styles.mobileMenuButton}
                          onClick={() => {
                            setTextEditModes({
                              highlight: !textEditModes.highlight,
                              strikeThrough: false,
                            });
                          }}
                        >
                          <Square className={styles.highlightSwatch} />
                          <Pen className={styles.smallIcon} />
                          <span className={styles.highlightLabel}>
                            {textEditModes.highlight
                              ? "Highlight enabled"
                              : "Highlight"}
                          </span>
                        </button>
                      )}
                      {testSettings.strikethrough && (
                        <button
                          className={styles.mobileMenuButton}
                          onClick={() => {
                            setTextEditModes({
                              highlight: false,
                              strikeThrough: !textEditModes.strikeThrough,
                            });
                          }}
                        >
                          <Strikethrough className={styles.strikeIconLarge} />
                          <span className={styles.normalText}>
                            {textEditModes.strikeThrough
                              ? "Strike through enabled"
                              : "Strike through"}
                          </span>
                        </button>
                      )}
                      {createdExam.tutorMode && (
                        <button
                          className={styles.mobileMenuButton}
                          onClick={() => {
                            handleViewAnswer();
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          <span className={styles.normalText}>View Answer</span>
                        </button>
                      )}
                      <button
                        className={styles.mobileMenuButton}
                        onClick={() => {
                          setSettingsOpen(true);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        <Cog className={styles.notesIcon} />
                        <span className={styles.normalText}>Settings</span>
                      </button>
                      {questionId == undefined && (
                        <button
                          className={styles.mobileMenuButton}
                          onClick={() => {
                            setQuestionListOpen(!questionListOpen);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          <ListOrdered className={styles.notesIcon} />
                          <span className={styles.normalText}>
                            {questionListOpen
                              ? "Hide question list"
                              : "Show question list"}
                          </span>
                        </button>
                      )}
                      {isReview && (
                        <button
                          className={styles.mobileMenuButton}
                          onClick={toggleHighYield}
                        >
                          <span
                            style={{
                              pointerEvents: "none",
                              display: "inline-flex",
                            }}
                          >
                            <Checkbox
                              checked={isHighYield}
                              setChecked={() => {}}
                              text={undefined}
                            />
                          </span>
                          <span className={styles.mobileMenuButtonLabel}>
                            High Yield
                          </span>
                        </button>
                      )}
                      <button
                        className={styles.mobileMenuButton}
                        onClick={toggleMarkForReview}
                      >
                        <span
                          style={{
                            pointerEvents: "none",
                            display: "inline-flex",
                          }}
                        >
                          <Checkbox
                            checked={markedForReview}
                            setChecked={() => {}}
                            text={undefined}
                          />
                        </span>
                        <PenSquareIcon className={styles.contrastIcon} />
                        <span className={styles.mobileMenuButtonLabel}>
                          Mark for review
                        </span>
                      </button>
                      <button
                        className={styles.mobileMenuButton}
                        onClick={() => {
                          setResourcesOpen(true);
                        }}
                      >
                        <BookCopy />
                        <span>Resources</span>
                      </button>
                      <button
                        className={styles.mobileMenuButton}
                        onClick={() => {
                          openFeedback();
                        }}
                      >
                        <Megaphone />
                        <span>Feedback</span>
                      </button>
                      <button
                        className={styles.mobileMenuButton}
                        onClick={() => {
                          if (isReview) review.onExit();
                          else setExitExamOpen(true);
                        }}
                      >
                        <DoorOpen />
                        <span>{isReview ? "Exit Review" : "Pause Test"}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                className={`${styles.actions} ${
                  createdExam.tutorMode ? styles.actionsTutor : ""
                }`}
              >
                <div className={styles.actionsLeft}>
                  {questionId == undefined && (
                    <button
                      className={styles.actionButton}
                      onClick={() => setQuestionListOpen(!questionListOpen)}
                      aria-pressed={questionListOpen}
                    >
                      <ListOrdered className={styles.actionIcon} />
                      <span className={styles.bold}>Questions</span>
                    </button>
                  )}
                  <button
                    className={styles.actionButton}
                    onClick={() => {
                      setNotesOpen(true);
                      setTextEditModes({
                        highlight: false,
                        strikeThrough: false,
                      });
                    }}
                  >
                    <NotepadText className={styles.actionIcon} />
                    <span className={styles.bold}>Notes</span>
                  </button>
                  {testSettings.highlighter && (
                    <button
                      className={styles.actionButton}
                      onClick={() => {
                        setTextEditModes({
                          highlight: !textEditModes.highlight,
                          strikeThrough: false,
                        });
                      }}
                    >
                      <Square className={styles.highlightSwatch} />
                      <Pen className={styles.smallIcon} />
                      <span className={styles.bold}>
                        {textEditModes.highlight
                          ? "Highlight enabled"
                          : "Highlight"}
                      </span>
                    </button>
                  )}
                  {testSettings.strikethrough && (
                    <button
                      className={styles.actionButton}
                      onClick={() => {
                        setTextEditModes({
                          highlight: false,
                          strikeThrough: !textEditModes.strikeThrough,
                        });
                      }}
                    >
                      <Strikethrough className={styles.strikeIcon} />
                      <span className={styles.bold}>
                        {textEditModes.strikeThrough
                          ? "Strike through enabled"
                          : "Strike through"}
                      </span>
                    </button>
                  )}
                </div>
                {/* view answer button */}
                {createdExam.tutorMode && (
                  <div className={styles.actionsCenter}>
                    <button
                      className={styles.actionButtonSpaced}
                      onClick={() => {
                        handleViewAnswer();
                      }}
                    >
                      <span className={styles.bold}>View Answer</span>
                    </button>
                  </div>
                )}
                <div className={styles.actionsRight}>
                  <button
                    className={styles.actionButtonSpaced}
                    onClick={() => {
                      setSettingsOpen(true);
                    }}
                  >
                    <Cog className={styles.actionIcon} />
                    <span className={styles.bold}>Settings</span>
                  </button>
                  {isReview && (
                    <button className={styles.markReview}>
                      <Checkbox
                        checked={isHighYield}
                        setChecked={toggleHighYield}
                        text={undefined}
                      />
                      <span className={styles.mobileLabel}>High Yield</span>
                    </button>
                  )}
                  <button className={styles.markReview}>
                    <Checkbox
                      checked={markedForReview}
                      setChecked={toggleMarkForReview}
                      text={undefined}
                    />

                    <span className={styles.mobileLabel}>Mark for review</span>
                  </button>
                </div>
              </div>
            )}
            {/* Center columns */}
            <div className={styles.centerOuter}>
              <div
                className={styles.centerFrame}
                onClick={handleQuestionImageClick}
              >
                <TestCenterColumns
                  //Mobile: CSS sizes the columns to fill the space between
                  //the action row and the footer (see test.module.scss)
                  centerColumSize={isMobile ? undefined : centerColumSize}
                  currentQuestion={currentQuestion}
                  questionError={questionIsError ? questionError : undefined}
                  getHighlightedText={getHighlightedText}
                  currentQuestionImage={imageSource}
                  questionLoading={isFetching || isLoading}
                  questionImageLoading={
                    questionImageLoading || questionImageFirstLoad
                  }
                  processQuestionText={processQuestionText}
                  GetShuffledAnswerArray={
                    isReview
                      ? () =>
                          GetUnmodifiedAnswerArray(
                            currentQuestion?.GetAnswer ?? [],
                          )
                      : GetShuffledAnswerArray
                  }
                  setExaminationState={
                    isReview || answerRevealed ? undefined : setExaminationState
                  }
                  examinationState={examinationState}
                  createdExam={createdExam}
                  handleAnswerChecked={handleAnswerChecked}
                  setScrollChecks={setScrollChecks}
                  ScrollChecks={scrollChecks}
                  questionType={
                    isReview
                      ? reviewQuestionType
                      : currentQuestion?.GetQuestion?.question_type
                  }
                  imagePath={currentQuestion?.GetQuestion?.image_path}
                  currentQuestionId={currentQuestionId}
                  completedAnswerArray={
                    isReview || answerRevealed
                      ? (examinationState.answersById[currentQuestionId] ?? [])
                      : undefined
                  }
                  showExplanations={
                    isReview || answerRevealed ? true : undefined
                  }
                  showChecksAndXes={
                    isReview || answerRevealed ? true : undefined
                  }
                  ShowMyAnswerMode={isReview || answerRevealed}
                  reviewData={reviewPrescriptionData}
                  prescriptionAnswerData={
                    answerRevealed ? prescriptionAnswerData : undefined
                  }
                  prescriptionAnswerLoading={
                    answerRevealed ? prescriptionAnswerLoading : undefined
                  }
                />
              </div>
            </div>
          </div>
        )}
        {examinationState.reviewMode && (
          <div className={styles.reviewOuter}>
            {/* Center column for review */}
            <div className={styles.reviewInner}>
              <div className={styles.reviewPanel}>
                <div
                  className={styles.reviewContent}
                  style={
                    isMobile ? undefined : { height: centerColumSizeReview }
                  }
                >
                  <div className={styles.reviewTitle}>
                    <span>Review Screen</span>
                  </div>
                  <div className={styles.reviewSectionHeader}>
                    <span>Instructions</span>
                  </div>
                  <div className={styles.reviewInstructions}>
                    <span>
                      Below is a summary of your answers. You can review your
                      questions in 3 different ways.
                    </span>
                    <span>
                      The buttons in the lower right-hand corner correspond to
                      these choices:
                    </span>
                    <ol className={styles.reviewList}>
                      <li>
                        Review All - Go through all of your questions and
                        answers.
                      </li>
                      <li>
                        Review Incomplete - Revisit the questions that are
                        incomplete.
                      </li>
                      <li>
                        Review Marked - Return to the questions that are marked
                        for review. (Click the checkbox to mark or unmark a
                        question.)
                      </li>
                    </ol>
                    <span>
                      You may also click on a question number to jump to it.
                    </span>
                    <span>
                      When you have finished your review, click on the End
                      Review button. Note that clicking on this button will end
                      the exam and you will no longer be able to return to the
                      Review Screen or any of the previous screens
                    </span>
                  </div>
                  <div className={styles.reviewSectionHeaderSpaced}>
                    <span>Section</span>
                  </div>
                  <div className={styles.reviewGrid}>
                    {createdExam.QuestionsForTest.map((key, index) => (
                      <div
                        className={styles.reviewItem}
                        key={`question-${key}`}
                      >
                        {/* Keyed on the mark so the box can't keep a stale
                            checked state when the mark changes */}
                        <CheckBox
                          key={`mark-${key}-${!!examinationState.reviewById[key]}`}
                          checked={!!examinationState.reviewById[key]}
                          setChecked={() => {
                            handleMarkCheckboxCheckedByIndex(key);
                          }}
                          text={undefined}
                        />
                        <button
                          key={key}
                          className={styles.reviewItemLink}
                          onClick={() => {
                            setExaminationState((prevState) => {
                              return {
                                ...prevState,
                                reviewMode: false,
                                currentQuestionIndex: index,
                              };
                            });
                          }}
                        >
                          {`Question ${index + 1}`}
                        </button>
                        {/* Same flag as the question list */}
                        {examinationState.reviewById[key] && (
                          <Flag
                            size={14}
                            aria-label="Marked"
                            style={{ marginLeft: 4, flexShrink: 0 }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {examinationState.reviewMode &&
          examinationState.reviewingAllQuestions && (
            <div className={styles.midBlock}></div>
          )}
        {/* Bottom Bar */}
        {!examinationState.reviewMode && (
          <div className={styles.bottomBar}>
            <div className={styles.bottomGroup}>
              {(examinationState.currentQuestionIndex ==
                createdExam.QuestionsForTest.length - 1 ||
                examinationState.reviewingAllQuestions ||
                examinationState.reviewingMarkedQuestions) &&
                !isMobile &&
                !isReview && (
                  <button
                    className={styles.reviewSubmitButton}
                    //Always straight to the review screen, never the answer
                    onClick={MoveToReviewMode}
                  >
                    <span>Review and Submit</span>
                  </button>
                )}
              {!isMobile && (
                <button
                  className={styles.secondaryButton}
                  onClick={() => {
                    setResourcesOpen(true);
                  }}
                >
                  <span>Resources</span>
                </button>
              )}
            </div>
            <div className={styles.bottomGroup}>
              {!isMobile && (
                <button
                  className={styles.secondaryButton}
                  onClick={() => {
                    if (isReview) review.onExit();
                    else setExitExamOpen(true);
                  }}
                >
                  <span>{isReview ? "Exit Review" : "Pause Test"}</span>
                </button>
              )}
            </div>
            {!isMobile && (
              <div className={styles.bottomGroupPlain}>
                <button
                  className={styles.primaryButton}
                  onClick={() => {
                    openFeedback();
                  }}
                >
                  <span>Feedback</span>
                </button>
              </div>
            )}
            <div className={styles.bottomGroupEnd}>
              <button
                className={styles.prevButton}
                onClick={() => {
                  if (examinationState.reviewingMarkedQuestions) {
                    MoveToPreviousMarkedPage();
                    return;
                  } else if (examinationState.reviewingIncompleteQuestions) {
                    MoveToPreviousUnansweredPage();
                    return;
                  }
                  if (examinationState.currentQuestionIndex >= 1) {
                    setExaminationState((prevState) => {
                      return {
                        ...prevState,
                        currentQuestionIndex:
                          prevState.currentQuestionIndex - 1,
                      };
                    });
                  }
                }}
              >
                <ChevronLeft />
                <span className={styles.prevText}>Previous</span>
              </button>
              <div>
                {" "}
                {(examinationState.currentQuestionIndex ==
                  createdExam.QuestionsForTest.length - 1 ||
                  examinationState.reviewingAllQuestions ||
                  examinationState.reviewingMarkedQuestions) &&
                  isMobile &&
                  !isReview && (
                    <button
                      className={styles.reviewSubmitButtonMobile}
                      onClick={MoveToReviewMode}
                    >
                      <span>Review and Submit</span>
                    </button>
                  )}
                <button
                  className={styles.secondaryWideButton}
                  onClick={() => {
                    setNavigator({ open: true });
                  }}
                >
                  <span>Navigator</span>
                </button>
              </div>
              <button
                className={
                  (examinationState.reviewingMarkedQuestions &&
                    markedKeyIndex + 1 < markedCount) ||
                  examinationState.currentQuestionIndex <
                    createdExam.QuestionsForTest.length - 1 ||
                  (!isReview && tutorRevealPending())
                    ? styles.nextButton
                    : styles.nextButtonDisabled
                }
                onClick={NextButtonPressed}
              >
                <span className={styles.nextText}>Next</span>
                <ChevronRight className={styles.nextIcon} />
              </button>
            </div>
          </div>
        )}
        {examinationState.reviewMode && (
          <div className={styles.bottomBarReview}>
            <div className={styles.bottomGroup}>
              <button
                className={styles.dangerButton}
                onClick={() => {
                  setWarningsState({
                    incomplete: false,
                    view: false,
                    end: true,
                  });
                }}
              >
                <span>End Review</span>
              </button>
            </div>
            <div className={styles.bottomGroupEnd}>
              <button
                className={styles.secondaryWideButton}
                onClick={() => {
                  if (
                    Object.values(examinationState.answersById).some(
                      // @ts-expect-error. this will always be an array
                      (value) => value.length == 0,
                    )
                  ) {
                    const emptyKeysAsString = Object.keys(
                      examinationState.answersById,
                    ).filter(
                      (key) =>
                        Array.isArray(examinationState.answersById[key]) &&
                        examinationState.answersById[key].length === 0,
                    );
                    const convertedEmptyStrings = emptyKeysAsString.map(Number);

                    const emptyKeys = createdExam.QuestionsForTest.filter(
                      (item) => convertedEmptyStrings.includes(item),
                    );
                    const firstIncomplete =
                      createdExam.QuestionsForTest.findIndex((key) =>
                        emptyKeys.includes(key),
                      );

                    setExaminationState((prevState) => {
                      return {
                        ...prevState,
                        reviewMode: false,
                        reviewingAllQuestions: false,
                        reviewingMarkedQuestions: false,
                        reviewingIncompleteQuestions: true,
                        currentQuestionIndex: firstIncomplete,
                        incompleteToBeReviewed: convertedEmptyStrings,
                      };
                    });
                  } else {
                    toast("There are no incomplete questions!");
                  }
                }}
              >
                <span>Review incomplete</span>
              </button>
              <button
                className={styles.secondaryWideButton}
                onClick={() => {
                  setExaminationState((prevState) => {
                    return {
                      ...prevState,
                      reviewMode: false,
                      reviewingAllQuestions: true,
                      reviewingMarkedQuestions: false,
                      reviewingIncompleteQuestions: false,
                      currentQuestionIndex: 0,
                    };
                  });
                }}
              >
                <span>Review All</span>
              </button>
              <button
                className={styles.secondaryWideButton}
                onClick={() => {
                  if (markedKeys.length > 0) {
                    const matchingIndex =
                      createdExam.QuestionsForTest.findIndex(
                        (question) => question == markedKeys[0],
                      );
                    setExaminationState((prevState) => {
                      return {
                        ...prevState,
                        reviewMode: false,
                        reviewingAllQuestions: false,
                        reviewingMarkedQuestions: true,
                        reviewingIncompleteQuestions: false,
                        currentQuestionIndex: matchingIndex,
                      };
                    });
                  } else {
                    toast("There are no marked questions!");
                  }
                }}
              >
                <span>Review Marked</span>
              </button>
            </div>
          </div>
        )}
        {/* Notes modal */}
        <Modal
          isOpen={notesOpen}
          onClose={() => {
            setNotesOpen(false);
          }}
          title={"Notes"}
          submit={undefined}
          onSubmit={undefined}
          closeHidden={undefined}
          fullWidth={undefined}
          pixelWidth={undefined}
          submitText={undefined}
          cancelText={undefined}
          xClose={undefined}
          submitDark={undefined}
          closeDark={undefined}
          buttonsSpaced={undefined}
        >
          <textarea
            className={styles.notesTextarea}
            placeholder="Notes..."
            onChange={handleNotesChange}
            value={
              examinationState.notesById[currentQuestionId]
                ? examinationState.notesById[currentQuestionId]
                : ""
            }
            autoComplete="off"
          />
        </Modal>
        {/* Question feedback */}
        <Modal
          isOpen={feedback.open}
          onClose={closeFeedback}
          title={`Feedback for question id: ${currentQuestionId}`}
          submit={feedback.status === "idle"}
          onSubmit={submitFeedback}
          closeHidden={undefined}
          fullWidth={undefined}
          pixelWidth={undefined}
          submitText={undefined}
          cancelText={undefined}
          xClose={undefined}
          submitDark={undefined}
          closeDark={undefined}
          buttonsSpaced={undefined}
        >
          {feedback.status === "success" || feedback.status === "error" ? (
            <div className={styles.feedbackResult}>
              <span>
                {feedback.status === "success"
                  ? "Thank you, your feedback has been sent!"
                  : "Failed to send feedback, please try again later."}
              </span>
            </div>
          ) : feedback.status === "loading" ? (
            <div>
              <span>Sending feedback...</span>
            </div>
          ) : (
            <textarea
              className={styles.feedbackTextarea}
              placeholder="Tell us if you have any thoughts about this question..."
              onChange={(event) => {
                const value = event.target.value;
                setFeedback((prev) => ({ ...prev, message: value }));
              }}
              value={feedback.message}
              autoComplete="off"
            />
          )}
        </Modal>
        {/* Settings pop-out */}
        <SettingsBar
          isTimed={isTimed}
          isOpen={settingsOpen}
          onClose={() => {
            setSettingsOpen(false);
          }}
        />
        {/* Navigator */}
        <Modal
          isOpen={navigator.open}
          onClose={() => {
            setNavigator({ open: false });
          }}
          title={"Navigator"}
          submit={undefined}
          onSubmit={undefined}
          fullWidth={true}
          closeHidden={undefined}
          pixelWidth={undefined}
          submitText={undefined}
          cancelText={undefined}
          xClose={undefined}
          submitDark={undefined}
          closeDark={undefined}
          buttonsSpaced={undefined}
        >
          <QuestionNavigator
            onClose={() => {
              setNavigator({ open: false });
            }}
            questionsIdData={createdExam.QuestionsForTest}
            examinationState={examinationState}
            NavigateToQuestion={(index) => {
              setExaminationState((prevState) => ({
                ...prevState,
                currentQuestionIndex: index,
              }));

              setExaminationState((prevState) => {
                return {
                  ...prevState,
                  currentQuestionIndex: index,
                };
              });
              setNavigator({ open: false });
            }}
            showNotes={() => {
              setNotesOpen(true);
            }}
          />
        </Modal>
        {/* Resources */}
        <Modal
          isOpen={resourcesOpen}
          onClose={() => {
            setResourcesOpen(false);
          }}
          title={"Resources"}
          cancelText={"Close"}
          pixelWidth={500}
          submit={undefined}
          closeHidden={undefined}
          onSubmit={undefined}
          fullWidth={undefined}
          submitText={undefined}
          xClose={undefined}
          submitDark={undefined}
          closeDark={undefined}
          buttonsSpaced={undefined}
        >
          <UniChart />
        </Modal>
        {/*Incomplete warning modal*/}
        <Modal
          isOpen={warningsState.incomplete}
          onClose={() => {
            setWarningsState({
              incomplete: false,
              view: false,
              end: false,
            });
            MoveForward();
          }}
          onSubmit={() => {
            setWarningsState({ incomplete: false, view: false, end: false });
          }}
          title={"Warning"}
          submit={true}
          closeHidden={false}
          submitText={"No"}
          cancelText={"Yes"}
          buttonsSpaced={true}
          xClose={true}
          closeDark={true}
          fullWidth={undefined}
          pixelWidth={undefined}
          submitDark={undefined}
        >
          <div className={styles.warningBody}>
            <div className={styles.warningRow}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="yellow"
                viewBox="0 0 24 24"
                stroke="black"
                strokeWidth="1.5"
                className={styles.warningIcon}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 2L2 22h20L12 2z"
                />
                <line
                  x1="12"
                  y1="8"
                  x2="12"
                  y2="14"
                  stroke="black"
                  strokeWidth="2"
                />
                <circle cx="12" cy="18" r="1" fill="black" />
              </svg>
              <span>{Warnings.Incomplete}</span>
            </div>
          </div>
        </Modal>
        {/*scroll warning modal*/}
        <Modal
          isOpen={warningsState.view}
          onSubmit={() => {}}
          onClose={() => {
            setWarningsState({ incomplete: false, view: false, end: false });
          }}
          title={"Warning"}
          submit={false}
          closeHidden={false}
          submitText={"No"}
          cancelText={"OK"}
          buttonsSpaced={true}
          xClose={true}
          closeDark={true}
          fullWidth={undefined}
          pixelWidth={undefined}
          submitDark={undefined}
        >
          <div className={styles.warningBody}>
            <div className={styles.warningRow}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="yellow"
                viewBox="0 0 24 24"
                stroke="black"
                strokeWidth="1.5"
                className={styles.warningIcon}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 2L2 22h20L12 2z"
                />
                <line
                  x1="12"
                  y1="8"
                  x2="12"
                  y2="14"
                  stroke="black"
                  strokeWidth="2"
                />
                <circle cx="12" cy="18" r="1" fill="black" />
              </svg>
              <span>{Warnings.View}</span>
            </div>
          </div>
        </Modal>
        {/*review warning modal*/}
        <Modal
          isOpen={warningsState.end}
          onClose={async () => {
            await SaveAndendExam();
          }}
          onSubmit={() => {
            setWarningsState({ incomplete: false, view: false, end: false });
          }}
          title={"Warning"}
          submit={true}
          closeHidden={false}
          submitText={"No"}
          cancelText={"Yes"}
          buttonsSpaced={true}
          xClose={true}
          closeDark={true}
          fullWidth={undefined}
          pixelWidth={undefined}
          submitDark={undefined}
        >
          <div className={styles.warningBody}>
            <div className={styles.warningRowTall}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="yellow"
                viewBox="0 0 24 24"
                stroke="black"
                strokeWidth="1.5"
                className={styles.warningIcon}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 2L2 22h20L12 2z"
                />
                <line
                  x1="12"
                  y1="8"
                  x2="12"
                  y2="14"
                  stroke="black"
                  strokeWidth="2"
                />
                <circle cx="12" cy="18" r="1" fill="black" />
              </svg>
              <span>{Warnings.End}</span>
            </div>
          </div>
        </Modal>
        {/*Timer ended Modal*/}
        <Modal
          isOpen={isTimed && testOutOfTime}
          onSubmit={() => {
            //Add more time
            AddMoreTime();
          }}
          onClose={async () => {
            //End exam
            await SaveAndendExam();
          }}
          title={"Out of time"}
          submitText={"Add Time"}
          cancelText={"End Exam"}
          buttonsSpaced={true}
          closeDark={true}
          submit={true}
          closeHidden={undefined}
          fullWidth={undefined}
          pixelWidth={undefined}
          xClose={undefined}
          submitDark={undefined}
        >
          <div className={styles.warningBody}>
            <span>
              You have run out of time, would you like to end your exam now Or
              add more time?
            </span>
          </div>
        </Modal>
        {/* No saving in a review */}
        {!isReview && (
          <ExamSaver
            examinationState={{ ...examinationState }}
            createdExam={{ ...createdExam }}
            numberOfQuestions={createdExam.QuestionsForTest.length}
            navigatorOpen={navigator.open}
            setErrorStatusCode={setErrorStatusCode}
            exitExamOpen={exitExamOpen}
            setExitExamOpen={setExitExamOpen}
            aboutToEndExam={examinationState.reviewMode}
            outOfTimeOpen={isTimed && testOutOfTime}
            /*questionType={currentQuestion?.GetQuestion?.question_type}*/
          />
        )}
        <ImageZoomModal
          imageSource={zoomedImageSource}
          isOpen={zoomedImageSource != null}
          onClose={closeImageZoom}
        />
      </div>
    </div>
  );
}

export default Test;